import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Word {
  t?: string;
  l?: number;
}

interface View {
  emoji: string;
  words: Word[];
  guesses: string[];
  /** Situation hint, appears after 3 misses. */
  hint?: string;
  answer?: string;
}

const COPY = {
  unknown_proverb: "Не знаю такого прислів'я — вибери з підказок",
  already_guessed: "Вже було",
};

export function Rebus({ date }: { date?: string | undefined }) {
  const g = useGame<View>("rebus", COPY, date);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("rebus").then((r) => setNames(r.names)).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const wrongs = won ? v.guesses.slice(0, -1) : v.guesses;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="РЕБУС" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mb-2 text-center text-xs text-tghint">яке прислів'я тут зашифроване? після 3 промахів - підказка</div>
        <div className="mx-auto flex max-w-[420px] flex-wrap items-center justify-center gap-1.5 rounded-2xl bg-tgsection px-4 py-6 text-[32px] leading-none">
          {v.emoji}
        </div>

        {/* Word skeleton: revealed words as text, hidden ones as length blocks. */}
        <div className="mx-auto mt-3 flex max-w-[420px] flex-wrap items-baseline justify-center gap-x-1.5 gap-y-2 text-[15px] leading-snug">
          {v.words.map((w, i) =>
            w.t !== undefined ? (
              <span key={i} className="font-semibold">
                {w.t}
              </span>
            ) : (
              <span
                key={i}
                className="inline-block select-none rounded-[3px]"
                style={{ width: `${Math.max(1, w.l ?? 3) * 0.55}em`, height: "0.9em", background: "rgba(128,128,128,0.4)" }}
              />
            ),
          )}
        </div>

        {!finished && v.hint && (
          <div className="mt-3 rounded-xl bg-warm/15 px-3 py-2 text-center text-sm">💡 {v.hint}</div>
        )}

        {wrongs.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {[...wrongs].reverse().map((t) => (
              <li key={t} className="rounded-xl bg-tgsection px-3 py-2 text-sm">
                ❌ {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <SuggestInput
          names={names}
          disabled={finished}
          busy={g.busy}
          exclude={guessed}
          onSubmit={(name) => void g.run(() => api.guess<View>("rebus", name, date))}
          placeholder="почни писати прислів'я…"
        />
        {!finished && v.guesses.length >= 2 && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("rebus", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Мудрець!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-xl">{v.emoji}</div>
              <div className="mt-2 text-base font-semibold">«{v.answer}»</div>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
